/* eslint-disable @typescript-eslint/no-floating-promises, ember/no-runloop */
import type Store from "@ember-data/store";
import type ApplicationInstance from "@ember/application/instance";
import { getOwner } from "@ember/owner";
import { bind } from "@ember/runloop";
import Service, { service } from "@ember/service";
import { capitalize } from "@ember/string";
import { tracked } from "@glimmer/tracking";
import type Patient from "parog-web/models/patient";
import { isTesting } from "@embroider/macros";
import { task } from "ember-concurrency";
import type CoreAdapter from "parog-web/adapters/core";
import type SemeiaSessionService from "parog-web/services/session";
import {
  OnlineConsultationError,
  OnlineConsultationNotAllowedError,
  OnlineConsultationNotFoundError,
  OnlineConsultationNotReadableError,
  OnlineConsultationNotSupportedError,
  OnlineConsultationTokenExpiredError,
  OnlineConsultationTypeError
} from "parog-web/utils/online-consultation-errors";
import type {
  LocalAudioTrack,
  LocalTrack,
  LocalVideoTrack,
  Participant,
  RemoteAudioTrack,
  RemoteVideoTrack,
  Room
} from "twilio-video";
import ControlTower from "wise-control-tower";

export default class OnlineConsultationService extends Service {
  @service declare session: SemeiaSessionService;
  @service declare store: Store;

  /**
   * A Twilio Auth Token suitable for video calls.
   * @see https://www.twilio.com/docs/video/javascript-getting-started#3-generate-an-access-token
   */
  @tracked authToken?: string;
  /**
   * Describes whether the remote participant has joined the room or not
   */
  @tracked hasParticipant = false;
  /**
   * Will be true after that the remote participant joined the room. It will then never be false.
   */
  @tracked hasStarted = false;
  /**
   * Will be true after the medic decided to close the consultation
   */
  @tracked isFinished = false;
  @tracked isLocalAudioEnabled = true;
  @tracked isLocalVideoEnabled = true;
  @tracked declare isRemoteAudioEnabled: boolean | null;
  @tracked declare isRemoteVideoEnabled;
  @tracked declare localAudioTrack: LocalTrack | null;
  @tracked declare localVideoTrack: LocalTrack | null;
  /**
   * Patient that participates to the online consultation
   */
  @tracked patient?: Patient;
  @tracked declare room: Room | null;

  applicationAdapter = (getOwner(this) as ApplicationInstance).lookup(
    "adapter:application"
  ) as CoreAdapter;
  /**
   * Describes whether video calls can be handled by the device/browser
   */
  declare isSupported: boolean;

  /**
   * True if the current user has joined the room
   */
  get isConnectedToRoom() {
    return !!this.room;
  }

  /**
   * True if the room has been opened on the server side
   */
  get isRoomOpened() {
    return this.authToken;
  }

  set isRoomOpened(value) {
    this.authToken = value;
  }

  constructor(owner) {
    super(owner);

    if (this.session.medic) {
      $(window).on("beforeunload", bind(this, this._onWindowClosed));
    }
  }

  _closeConsultation() {
    this.isFinished = true;

    if (this.room) {
      this.room.disconnect();
    }

    this.authToken = undefined;
    this.hasParticipant = false;
    this.hasStarted = false;

    this.room = null;
    this.localVideoTrack = null;
    this.localAudioTrack = null;
    this.isLocalVideoEnabled = true;
    this.isLocalAudioEnabled = true;
    this.isRemoteVideoEnabled = null;
    this.isRemoteAudioEnabled = null;

    // The properties patient and isFinished are not reset because still usefull
    // They will be reset on time when the pane will be hidden and then redisplayed
  }

  async _createNewConsultationToken(): Promise<string> {
    const body = {
      data: {
        attributes: {
          "patient-id": this.patient?.id
        }
      }
    };

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", "/api/v1/online_consultation", true);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.onload = function () {
        if (this.status === 201) {
          const jsonResponse = JSON.parse(this.responseText);
          const token = jsonResponse.data?.attributes?.["medic-auth-token"];
          resolve(token);
        } else {
          reject(new OnlineConsultationError(xhr.statusText));
        }
      };

      for (const key in this.applicationAdapter.headers) {
        if (this.applicationAdapter.headers.hasOwnProperty(key)) {
          xhr.setRequestHeader(key, this.applicationAdapter.headers[key]);
        }
      }
      xhr.send(JSON.stringify(body));
    });
  }

  _createOnlineConsultationEndedJE() {
    this.store
      .createRecord("online-consultation-ended-journal-event", {
        patient: this.patient,
        occurredAt: new Date()
      })
      .save();
  }

  /**
   * @param participant Participant that just disconnected from the room
   */
  _createOnlineConsultationEndedJEOnDisconnect(participant: Participant) {
    // If there is no more external participant and the one who just left was the patient, create the JE.
    // Thus, this will be executed only on medic's side and will avoid multiple creation of JE
    if (
      (this.room as Room).participants.size === 0 &&
      participant.identity.startsWith("patient-")
    ) {
      this._createOnlineConsultationEndedJE();
    }
  }

  _createOnlineConsultationStartedJEIfNeeded() {
    // The number of participants (room.participants) represents the number of external participant.
    // The current user is not counted as a participant, which means that we should  always and only have 0 or 1 participant
    // Thus, we create a JE when there was no participant before, and the new participant is a patient
    // So it means this will be triggered only for the medic when the patients joins the consultation
    // or if he was already there, in the room before the medic joined.

    // This prevents creating multiple JEs if the patient joins twice the consultation
    if (this.hasParticipant) return;

    if (this._isPatientParticipating()) {
      this.store
        .createRecord("online-consultation-started-journal-event", {
          patient: this.patient,
          occurredAt: new Date()
        })
        .save();
    }
  }

  _handleRemoteTracks(participant, showTrackBigAudio, showTrackBigVideo) {
    participant.tracks.forEach(publication => {
      if (publication.isSubscribed) {
        const track = publication.track;
        if (track.kind === "video") {
          showTrackBigVideo(track);
        }
        if (track.kind === "audio") {
          showTrackBigAudio(track);
        }
      }
    });

    participant.on("trackSubscribed", track => {
      this._updateRemoteEnabledTracks({ track, enabled: true });
      if (track.kind === "video") {
        showTrackBigVideo(track);
      }
      if (track.kind === "audio") {
        showTrackBigAudio(track);
      }
    });

    participant.on("trackUnsubscribed", track => {
      this._updateRemoteEnabledTracks({ track, enabled: false });
      track.detach().forEach(element => element.remove());
    });
  }

  _isPatientParticipating() {
    let isPatientParticipating = false;
    if (this.room) {
      this.room.participants.forEach(participant => {
        isPatientParticipating =
          isPatientParticipating || participant.identity.startsWith("patient-");
      });
    }
    return isPatientParticipating;
  }

  // --- Twilio workflow Helpers
  _onWindowClosed() {
    if (this._isPatientParticipating()) {
      this._createOnlineConsultationEndedJE();
    }
  }

  _updateHasParticipant() {
    this.hasParticipant = (this.room as Room).participants.size > 0;
    this.hasStarted = this.hasParticipant ? true : this.hasStarted;
  }

  // --- Helpers
  _updatePublication(track: LocalTrack | null, publish: boolean) {
    if (publish) {
      (this.room as Room).localParticipant.publishTrack(track as LocalTrack);
    } else {
      (this.room as Room).localParticipant.tracks.forEach(publication => {
        if (publication.track.name === (track as LocalTrack).name) {
          publication.unpublish();
        }
      });
    }
  }

  _updateRemoteEnabledTracks({ track, enabled }) {
    this[`isRemote${capitalize(track.kind)}Enabled`] = enabled;
  }

  /**
   * This functions allows you to join a room and setup everything (video and audio).
   * An authToken must have been set in the service before.
   * @param insertSmallVideoTrack - Callback called when the video of the caller can be displayed
   * @param insertBigAudioTrack - Callback called when the audio of the recipient can be displayed
   * @param insertBigVideoTrack - Callback called when the video of the recipient can be displayed
   */
  async connectToRoom(
    insertSmallVideoTrack: (localTrack: LocalTrack) => void,
    insertBigAudioTrack: () => void,
    insertBigVideoTrack: () => void
  ) {
    if (!this.authToken) {
      const error = new OnlineConsultationError(
        "No auth token set before calling connectToRoom"
      );
      ControlTower.captureException(error);
      throw error;
    }

    const twilioVideo = await import("twilio-video");

    // Only get the `isSupported` from twilioVideo
    // IF we are NOT in testing mode
    // OR this.isSupported has not been set yet
    if (!isTesting()) this.isSupported = twilioVideo.isSupported;
    else if (this.isSupported === undefined)
      this.isSupported = twilioVideo.isSupported;
    else {
      // The service is stubbed by the test and this.isSupported has been set, do not change it
    }

    if (!this.isSupported) {
      throw new OnlineConsultationNotSupportedError();
    }

    try {
      const room = await twilioVideo.connect(this.authToken, {
        audio: true,
        video: { width: 640 }
      });
      this.room = room;

      this.room.localParticipant.tracks.forEach(publication => {
        if (publication.kind === "video") {
          this.localVideoTrack = publication.track;
          insertSmallVideoTrack(publication.track);
        } else if (publication.kind === "audio") {
          this.localAudioTrack = publication.track;
        }
      });

      this._createOnlineConsultationStartedJEIfNeeded();
      this._updateHasParticipant();

      this.room.participants.forEach(participant => {
        this._handleRemoteTracks(
          participant,
          insertBigAudioTrack,
          insertBigVideoTrack
        );
      });

      this.room.on("participantConnected", participant => {
        this._createOnlineConsultationStartedJEIfNeeded();
        this._updateHasParticipant();
        this._handleRemoteTracks(
          participant,
          insertBigAudioTrack,
          insertBigVideoTrack
        );
      });

      this.room.on("participantDisconnected", participant => {
        participant.tracks.forEach(publication => {
          if (publication.track) {
            // publication.track is of type RemoteAudioTrack | RemoteVideoTrack | RemoteDataTrack;
            // but `detach` method is not defined on RemoteDataTrack type
            const attachedElements = (
              publication.track as RemoteAudioTrack | RemoteVideoTrack
            ).detach();
            attachedElements.forEach(element => element.remove());
          }
        });

        this._createOnlineConsultationEndedJEOnDisconnect(participant);
        this._updateHasParticipant();
      });

      this.room.on("disconnected", room => {
        // Detach the local media elements
        room.localParticipant.tracks.forEach(publication => {
          // publication.track is of type LocalAudioTrack | LocalVideoTrack | LocalDataTrack;
          // but `detach` method is not defined on LocalDataTrack type
          const attachedElements = (
            publication.track as LocalAudioTrack | LocalVideoTrack
          ).detach();
          attachedElements.forEach(element => element.remove());
        });
      });
    } catch (error) {
      if (error.name === "TwilioError" && error.code == 20104) {
        // This is a token expiration error
        // @see https://www.twilio.com/docs/api/errors/20104
        throw new OnlineConsultationTokenExpiredError(error.message);
      } else if (error.name === "NotAllowedError") {
        throw new OnlineConsultationNotAllowedError();
      } else if (error.name === "NotFoundError") {
        throw new OnlineConsultationNotFoundError();
      } else if (error.name === "NotReadableError") {
        throw new OnlineConsultationNotReadableError();
      } else if (error.name === "TypeError") {
        throw new OnlineConsultationTypeError();
      } else {
        ControlTower.captureException(error);
        throw new OnlineConsultationError(error.message);
      }
    }
  }

  finishConsultation() {
    if (this.session.medic && this._isPatientParticipating()) {
      this._createOnlineConsultationEndedJE();
    }

    this._closeConsultation();
  }

  /**
   * Retrieve a twilio auth token, open a room and invit the patient for an online consultation
   * Only for the medic
   */
  invitePatientForOnlineConsultation = task(async patient => {
    this.patient = patient;
    await this.store
      .createRecord("online-consultation-room-opened-journal-event", {
        patient: this.patient,
        occurredAt: new Date()
      })
      .save();
    this.authToken = await this._createNewConsultationToken();
  });

  reset() {
    this.patient = undefined;
    this.isFinished = false;
  }

  toggleLocalAudio() {
    this.isLocalAudioEnabled = !this.isLocalAudioEnabled;
    this._updatePublication(this.localAudioTrack, this.isLocalAudioEnabled);
  }

  toggleLocalVideo() {
    this.isLocalVideoEnabled = !this.isLocalVideoEnabled;
    this._updatePublication(this.localVideoTrack, this.isLocalVideoEnabled);
  }
}

declare module "@ember/service" {
  interface Registry {
    "online-consultation": OnlineConsultationService;
  }
}
